import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { verbConjugations } from '../data/conjugations';
import { Plus, BookOpen, Volume2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const VerbConjugationPanel = ({ verb, language = 'de', onAddToTraining }) => {
  const [selectedTense, setSelectedTense] = useState('present');
  const { toast } = useToast();

  const conjugationData = verbConjugations[language]?.[verb];

  if (!conjugationData) {
    return null;
  }

  const tenses = [
    { key: 'present', label: 'Präsens', labelEn: 'Present' },
    { key: 'past', label: 'Präteritum', labelEn: 'Past' },
    { key: 'perfect', label: 'Perfekt', labelEn: 'Perfect' }
  ];

  const getTenseLabel = (tense) => {
    const tenseData = tenses.find(t => t.key === tense);
    return language === 'de' ? tenseData?.label : tenseData?.labelEn;
  };

  const handleAddToTraining = () => {
    if (onAddToTraining) {
      onAddToTraining({
        type: 'verb_conjugation',
        verb: verb,
        language: language,
        conjugations: conjugationData
      });
    }
    
    toast({
      title: "Verb hinzugefügt",
      description: `"${verb}" wurde zum Training hinzugefügt.`,
    });
  };

  const speakConjugation = (text) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = language === 'de' ? 'de-DE' : language === 'en' ? 'en-US' : 'fr-FR';
      utterance.rate = 0.8;
      speechSynthesis.speak(utterance);
    }
  };

  return (
    <Card className="w-full border-0 shadow-lg">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-bold text-slate-900">
              {conjugationData.infinitive}
            </CardTitle>
            <p className="text-sm text-slate-600 mt-1">
              {language === 'de' ? 'Verb' : 'Verb'} • {conjugationData.meaning}
            </p>
          </div>
          <Button
            size="sm"
            className="bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
            onClick={handleAddToTraining}
          >
            <Plus className="h-4 w-4 mr-2" />
            Zum Training hinzufügen
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <Tabs value={selectedTense} onValueChange={setSelectedTense}>
          <TabsList className="grid w-full grid-cols-3">
            {tenses.map((tense) => (
              <TabsTrigger key={tense.key} value={tense.key} className="text-xs">
                {getTenseLabel(tense.key)}
              </TabsTrigger>
            ))}
          </TabsList>

          {tenses.map((tense) => (
            <TabsContent key={tense.key} value={tense.key} className="mt-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-slate-900">
                    {getTenseLabel(tense.key)}
                  </h4>
                  <Badge variant="outline" className="text-[#4ECDC4] border-[#4ECDC4]">
                    {tense.key}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-1 gap-2">
                  {Object.entries(conjugationData[tense.key] || {}).map(([person, form]) => (
                    <div
                      key={person}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-slate-700 min-w-[60px]">
                          {person}
                        </span>
                        <span className="text-sm font-bold text-slate-900">
                          {form}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => speakConjugation(`${person} ${form}`)}
                      >
                        <Volume2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* Usage Examples */}
        <div className="mt-6 pt-4 border-t border-slate-200">
          <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Beispiele
          </h4>
          <div className="space-y-2">
            {conjugationData.examples?.map((example, index) => (
              <div key={index} className="p-2 bg-blue-50 rounded text-sm">
                <p className="text-slate-800">{example}</p>
              </div>
            )) || (
              <div className="space-y-2">
                <div className="p-2 bg-blue-50 rounded text-sm">
                  <p className="text-slate-800">
                    <strong>{Object.keys(conjugationData.present)[0]} {Object.values(conjugationData.present)[0]}</strong> einen Hund.
                  </p>
                </div>
                <div className="p-2 bg-blue-50 rounded text-sm">
                  <p className="text-slate-800">
                    <strong>{Object.keys(conjugationData.past)[0]} {Object.values(conjugationData.past)[0]}</strong> gestern Besuch.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VerbConjugationPanel;